/**
 * Mini TV viewer: presentation + execution only (no header).
 * Presentational: receives phase, event, results, videoUrl from parent (virtual-web).
 * Supports Racing, Baseball, Football per sport_id.
 * videoUrl is HLS (api.video) – we use HLS.js when needed.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Emitter from 'shared/events/emitter';
import EVENT_NAMES from 'shared/constants/events';
import VideoEvolutions from 'features/video/components/racing/VideoEvolutions';
import OverlayContainer from 'features/video/components/overlays/OverlayContainer';
import MiniTvBaseballExecution from 'features/video/components/mini-tv/MiniTvBaseballExecution';
import MiniTvFootballExecution from 'features/video/components/mini-tv/MiniTvFootballExecution';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { withBasePath } from 'shared/utils/runtimeBasePath';
import { useHlsOrSrc } from 'features/video/components/mini-tv/useHlsOrSrc';
import MiniTvUiOverlayContext from './MiniTvUiOverlayContext';
import { FALLBACK_TEAM1_ID, FALLBACK_TEAM2_ID } from 'features/video/components/mini-tv/miniTvDefaults';
import 'features/video/components/mini-tv/mini-tv.css';

const PLACE_LABELS_SHOW_AFTER_SEC = 10;
const PLACE_LABELS_HIDE_BEFORE_END_SEC = 6;
const JOIN_LATE_LOADER_TIMEOUT_MS = 10000;

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];
const BASEBALL_SPORT_ID = 40;
const FOOTBALL_SPORT_ID = 50;

function isRacing(sportId) {
  return sportId != null && RACING_SPORT_IDS.includes(Number(sportId));
}
function isBaseball(sportId) {
  return Number(sportId) === BASEBALL_SPORT_ID;
}
function isFootball(sportId) {
  return Number(sportId) === FOOTBALL_SPORT_ID;
}

/** Sport-based background for presentation (racing: dogs/horses; baseball: baseball background; football: football background; same as platform-tv). */
function getPresentationBackgroundImage(sportId) {
  const id = Number(sportId);
  if (id === 20 || id === 21) return `url(${withBasePath('/images/racing/horses-background.png')})`;
  if (id === 40) return `url(${withBasePath('/images/baseball/background.png')})`;
  if (id === 50) return `url(${withBasePath('/images/football/background.png')})`;
  return `url(${withBasePath('/images/racing/dogs-background.png')})`;
}

/** Racing: same structure as platform-tv RacingPresentationScreen — background + centered NO MORE BETS. */
function RacingPresentation({ sportId }) {
  const { t } = useTranslation();
  const backgroundImage = sportId != null ? getPresentationBackgroundImage(sportId) : `url(${withBasePath('/images/racing/dogs-background.png')})`;
  return (
    <div
      className="c7-mini-tv-presentation c7-mini-tv-presentation--racing"
      style={{ backgroundImage, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="racing-presentation">
        <div className="racing-presentation-content">
          <div className="racing-presentation-text">{t('NO_MORE_BETS')}</div>
        </div>
      </div>
    </div>
  );
}

/** Baseball: same structure as platform-tv BaseballPresentationScreen — teams, batter block, gradient. */
function BaseballPresentation({ event, eventResults }) {
  const { t } = useTranslation();
  const [runAnim, setRunAnim] = React.useState(false);
  const team1Id = eventResults?.team1?.team_id ?? FALLBACK_TEAM1_ID;
  const team2Id = eventResults?.team2?.team_id ?? FALLBACK_TEAM2_ID;
  const batterName = eventResults?.batter_name ?? event?.batter_name ?? '';

  React.useEffect(() => {
    setRunAnim(false);
    const id = window.setTimeout(() => setRunAnim(true), 30);
    return () => window.clearTimeout(id);
  }, [event?.event_id]);

  return (
    <div
      className="c7-mini-tv-presentation c7-mini-tv-presentation--baseball"
      style={{ backgroundImage: `url(${withBasePath('/images/baseball/background.png')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className={['c7-mini-tv-baseball-presentation__root', runAnim ? 'c7-mini-tv-baseball-presentation__run' : ''].filter(Boolean).join(' ')}>
        <div className="c7-mini-tv-baseball-presentation__team c7-mini-tv-baseball-presentation__team2">
          <img src={withBasePath(`/images/baseball-teams/${team2Id}.svg`)} alt="team2" style={{ width: '100%', height: '100%' }} />
        </div>
        <div className="c7-mini-tv-baseball-presentation__team c7-mini-tv-baseball-presentation__team1">
          <img src={withBasePath(`/images/baseball-teams/${team1Id}.svg`)} alt="team1" style={{ width: '100%', height: '100%' }} />
        </div>
        <div className="c7-mini-tv-baseball-presentation__batter-block">
          <span className="c7-mini-tv-baseball-presentation__no-more-bets">{t('NO_MORE_BETS') || 'NO MORE BETS'}</span>
          <span className="c7-mini-tv-baseball-presentation__batter-name">{batterName}</span>
        </div>
      </div>
    </div>
  );
}

/** Football: same structure as platform-tv FootballPresentationScreen — background, panel, teams, VS, NO MORE BETS. */
function FootballPresentation({ event, eventResults }) {
  const { t } = useTranslation();
  const homeTeam = event?.home_team ?? event?.events_football3d?.home_team ?? eventResults?.home_team;
  const awayTeam = event?.away_team ?? event?.events_football3d?.away_team ?? eventResults?.away_team;
  const homeTeamCode = homeTeam?.code ?? event?.home_team_code ?? eventResults?.home_team_code;
  const awayTeamCode = awayTeam?.code ?? event?.away_team_code ?? eventResults?.away_team_code;
  const homeTeamName = homeTeam?.name ?? event?.home_team_name ?? eventResults?.home_team_name;
  const awayTeamName = awayTeam?.name ?? event?.away_team_name ?? eventResults?.away_team_name;

  if (!homeTeamCode || !awayTeamCode) {
    return (
      <div className="c7-mini-tv-presentation c7-mini-tv-presentation--football">
        <div className="c7-mini-tv-football-presentation__error">Missing team data</div>
      </div>
    );
  }

  return (
    <div className="c7-mini-tv-presentation c7-mini-tv-presentation--football c7-mini-tv-football-presentation">
      <div className="c7-mini-tv-football-presentation__bg" style={{ backgroundImage: `url(${withBasePath('/images/football/background.png')})` }}>
        <div className="c7-mini-tv-football-presentation__pitch-overlay" />
      </div>
      <div className="c7-mini-tv-football-presentation__panel">
        <div className="c7-mini-tv-football-presentation__content">
          <div className="c7-mini-tv-football-presentation__team c7-mini-tv-football-presentation__team--home c7-mini-tv-animate-slide-in-left">
            <div className="c7-mini-tv-football-presentation__team-badge">
              <img
                src={getFootball3dTeamLogoUrl(homeTeamCode)}
                alt={homeTeamName || homeTeamCode}
                onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
              />
            </div>
            <div className="c7-mini-tv-football-presentation__team-name">{homeTeamCode}</div>
          </div>
          <div className="c7-mini-tv-football-presentation__center c7-mini-tv-animate-fade-in">
            <div className="c7-mini-tv-football-presentation__vs-badge">VS</div>
            <div className="c7-mini-tv-football-presentation__kick-off-text">{t('NO_MORE_BETS') || 'NO MORE BETS'}</div>
          </div>
          <div className="c7-mini-tv-football-presentation__team c7-mini-tv-football-presentation__team--away c7-mini-tv-animate-slide-in-right">
            <div className="c7-mini-tv-football-presentation__team-badge">
              <img
                src={getFootball3dTeamLogoUrl(awayTeamCode)}
                alt={awayTeamName || awayTeamCode}
                onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
              />
            </div>
            <div className="c7-mini-tv-football-presentation__team-name">{awayTeamCode}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Top-right event box: portaled to the UI overlay layer outside the canvas. */
function PortaledEventBox({ eventId }) {
  const { t } = useTranslation();
  const uiOverlayRef = React.useContext(MiniTvUiOverlayContext);
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => {
    if (uiOverlayRef?.current && !target) setTarget(uiOverlayRef.current);
  }, [uiOverlayRef?.current, target]);
  if (eventId == null || String(eventId).trim() === '') return null;
  const box = (
    <div className="event-box" aria-label={t('EVENT_NUMBER')}>
      <div className="event-info">
        <span className="event-box-event-id-label">{t('EVENT_NUMBER')}</span>
        <br />
        <span className="hash">#</span>
        <span className="event-box-event-id">{String(eventId)}</span>
      </div>
      <div className="event-box-logo">
        <img src={withBasePath('/images/baseball/event-box-logo.png')} alt="Event logo" />
      </div>
    </div>
  );
  if (target) return ReactDOM.createPortal(box, target);
  return box;
}

function RacingExecution({ eventId, eventResults, videoUrl, avh, showevolution, videoRef, sportId, onExecutionEnded, initialSeekTime, muted }) {
  useHlsOrSrc(videoRef, videoUrl, avh, `${String(eventId ?? '')}|${String(sportId ?? '')}|${String(videoUrl ?? '')}|${String(avh ?? '')}`);
  const videoId = eventResults?.video_id ?? null;
  const isHorses = Number(sportId) === 20 || Number(sportId) === 21;
  const [showPlaceLabels, setShowPlaceLabels] = React.useState(false);
  const replayedRef = React.useRef(false);
  const initialSeekAppliedRef = React.useRef(false);
  const isJoinLate = initialSeekTime != null && Number(initialSeekTime) > 0;
  const [joinLateReady, setJoinLateReady] = React.useState(() => !isJoinLate);
  const startReplay = eventResults?.start_replay != null ? parseFloat(eventResults.start_replay) : NaN;
  const endReplay = eventResults?.end_replay != null ? parseFloat(eventResults.end_replay) : NaN;
  const hasReplayBounds = !Number.isNaN(startReplay) && !Number.isNaN(endReplay);

  React.useEffect(() => {
    replayedRef.current = false;
    initialSeekAppliedRef.current = false;
    setShowPlaceLabels(false);
    setJoinLateReady(!(initialSeekTime != null && Number(initialSeekTime) > 0));
  }, [eventId, initialSeekTime]);

  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !videoUrl || initialSeekAppliedRef.current) return;
    const seekSec = initialSeekTime != null && Number(initialSeekTime) > 0 ? Number(initialSeekTime) : null;
    const onCanPlay = () => {
      if (seekSec == null) {
        setJoinLateReady(true);
        video.play().catch((err) => console.warn('[MiniTv] play() rejected:', err?.message || err));
        return;
      }
      if (initialSeekAppliedRef.current) return;
      initialSeekAppliedRef.current = true;
      const duration = video.duration;
      const targetTime = Number.isFinite(duration) && duration > 0 ? Math.min(seekSec, duration) : seekSec;
      video.currentTime = targetTime;
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        setJoinLateReady(true);
      };
      video.addEventListener('seeked', onSeeked);
      if (hasReplayBounds) {
        if (targetTime >= endReplay) {
          replayedRef.current = false;
        } else if (targetTime >= startReplay && targetTime < endReplay) {
          replayedRef.current = true;
          video.defaultPlaybackRate = 0.3;
          video.playbackRate = 0.3;
        }
      }
      video.play().catch((err) => console.warn('[MiniTv] play() rejected:', err?.message || err));
    };
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 2) onCanPlay();
    return () => video.removeEventListener('canplay', onCanPlay);
  }, [videoRef, videoUrl, initialSeekTime, hasReplayBounds, startReplay, endReplay]);

  React.useEffect(() => {
    if (!isJoinLate) return;
    const t = window.setTimeout(() => setJoinLateReady(true), JOIN_LATE_LOADER_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [isJoinLate]);

  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !isJoinLate) return;
    const onError = () => setJoinLateReady(true);
    video.addEventListener('error', onError);
    return () => video.removeEventListener('error', onError);
  }, [videoRef, isJoinLate]);

  const onTimeUpdate = React.useCallback(
    (e) => {
      const video = e?.target;
      if (!video || !eventResults) return;
      const time = video.currentTime;
      Emitter.emit(EVENT_NAMES.MEDIA.EVOLUTION_PROCEED, {
        time,
        event_results: { ...eventResults, sport_id: eventResults.sport_id },
      });
      if (videoId) {
        Emitter.emit(EVENT_NAMES.MEDIA.TIMEUPDATE_PROCEED + '-' + videoId, { time });
      }
      if (replayedRef.current && time >= endReplay) {
        video.pause();
        replayedRef.current = false;
        onExecutionEnded?.();
      }
    },
    [eventResults, videoId, endReplay, onExecutionEnded]
  );

  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !onExecutionEnded) return;
    const handleEnded = () => {
      if (hasReplayBounds && !replayedRef.current) {
        replayedRef.current = true;
        video.defaultPlaybackRate = 0.3;
        video.playbackRate = 0.3;
        video.currentTime = Number(startReplay.toFixed(2));
        video.play().catch((err) => console.warn('[MiniTv] play() rejected:', err?.message || err));
        return;
      }
      onExecutionEnded();
    };
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [videoRef, hasReplayBounds, startReplay, onExecutionEnded]);

  React.useEffect(() => {
    if (!isHorses || !videoRef?.current) return;
    const video = videoRef.current;
    const updateVisibility = () => {
      const currentTime = video.currentTime;
      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        setShowPlaceLabels(currentTime >= PLACE_LABELS_SHOW_AFTER_SEC);
        return;
      }
      const inWindow =
        currentTime >= PLACE_LABELS_SHOW_AFTER_SEC &&
        (duration - currentTime) >= PLACE_LABELS_HIDE_BEFORE_END_SEC;
      setShowPlaceLabels(inWindow);
    };
    video.addEventListener('timeupdate', updateVisibility);
    video.addEventListener('loadedmetadata', updateVisibility);
    video.addEventListener('durationchange', updateVisibility);
    updateVisibility();
    return () => {
      video.removeEventListener('timeupdate', updateVisibility);
      video.removeEventListener('loadedmetadata', updateVisibility);
      video.removeEventListener('durationchange', updateVisibility);
    };
  }, [isHorses, videoRef, eventId]);

  return (
    <div className="c7-mini-tv-execution">
      <div className="c7-mini-tv-media-wrapper">
        <video
          ref={videoRef}
          id="c7-mini-tv-event-video"
          src={videoUrl && !videoUrl.includes('m3u8') ? videoUrl : ''}
          playsInline
          muted={muted}
          onTimeUpdate={onTimeUpdate}
        />
        {videoId && (
          <div className="c7-mini-tv-overlay-wrapper">
            <OverlayContainer video_id={videoId} scale_factor={1} />
          </div>
        )}
        {isHorses && (
          <div
            className={`c7-mini-tv-horses-place-labels${showPlaceLabels ? '' : ' c7-mini-tv-horses-place-labels--hidden'}${Number(sportId) === 21 ? ' c7-mini-tv-horses-place-labels--horses8' : ''}`}
            aria-hidden
          >
            <div className="c7-mini-tv-horses-place-labels__patch c7-mini-tv-horses-place-labels__patch--1">1st</div>
            <div className="c7-mini-tv-horses-place-labels__patch c7-mini-tv-horses-place-labels__patch--2">2nd</div>
            <div className="c7-mini-tv-horses-place-labels__patch c7-mini-tv-horses-place-labels__patch--3">3rd</div>
            <div className="c7-mini-tv-horses-place-labels__patch c7-mini-tv-horses-place-labels__patch--4">4th</div>
          </div>
        )}
        {showevolution && eventResults?.sport_id != null && (
          <div className="c7-mini-tv-evolution-wrapper">
            <VideoEvolutions video_id={eventResults?.video_id} event_results={eventResults} />
          </div>
        )}
        {!joinLateReady && isJoinLate && (
          <div className="c7-mini-tv-join-late-loader" aria-hidden>
            <div className="c7-mini-tv-join-late-loader__spinner" />
          </div>
        )}
        <PortaledEventBox eventId={eventId} />
      </div>
    </div>
  );
}

function BaseballExecution({ eventId, videoUrl, avh, videoRef, onExecutionEnded, muted }) {
  useHlsOrSrc(videoRef, videoUrl, avh, `${String(eventId ?? '')}|${String(videoUrl ?? '')}|${String(avh ?? '')}`);
  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !onExecutionEnded) return;
    const handleEnded = () => onExecutionEnded();
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [videoRef, onExecutionEnded]);
  return (
    <div className="c7-mini-tv-execution">
      <div className="c7-mini-tv-media-wrapper">
        <video ref={videoRef} id="c7-mini-tv-event-video" src={videoUrl && !videoUrl.includes('m3u8') ? videoUrl : ''} playsInline muted={muted} />
        <PortaledEventBox eventId={eventId} />
      </div>
    </div>
  );
}

function FootballExecution({ eventId, videoUrl, avh, videoRef, onExecutionEnded, muted }) {
  useHlsOrSrc(videoRef, videoUrl, avh, `${String(eventId ?? '')}|${String(videoUrl ?? '')}|${String(avh ?? '')}`);
  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !onExecutionEnded) return;
    const handleEnded = () => onExecutionEnded();
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [videoRef, onExecutionEnded]);
  return (
    <div className="c7-mini-tv-execution">
      <div className="c7-mini-tv-media-wrapper">
        <video ref={videoRef} id="c7-mini-tv-event-video" src={videoUrl && !videoUrl.includes('m3u8') ? videoUrl : ''} playsInline muted={muted} />
        <PortaledEventBox eventId={eventId} />
      </div>
    </div>
  );
}

function MiniTvGenericVideo({ eventId, videoRef, videoUrl, avh, onExecutionEnded, muted }) {
  useHlsOrSrc(videoRef, videoUrl, avh, `${String(eventId ?? '')}|${String(videoUrl ?? '')}|${String(avh ?? '')}`);
  React.useEffect(() => {
    const video = videoRef?.current;
    if (!video || !onExecutionEnded) return;
    const handleEnded = () => onExecutionEnded();
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [videoRef, onExecutionEnded]);
  return (
    <div className="c7-mini-tv-execution">
      <div className="c7-mini-tv-media-wrapper">
        <video ref={videoRef} id="c7-mini-tv-event-video" src={videoUrl && !videoUrl.includes('m3u8') ? videoUrl : ''} playsInline muted={muted} />
        <PortaledEventBox eventId={eventId} />
      </div>
    </div>
  );
}

export default function MiniTvViewer({
  phase,
  event,
  eventResults,
  videoUrl,
  videoUrls,
  avh,
  sportId,
  onVideoReady,
  onExecutionEnded,
  showevolution = 1,
  joinElapsedSeconds,
  initialSeekTime,
  initialSegmentIndex,
  initialSegmentSeekSeconds,
  otherWeekEvents,
  weekResults,
  muted = true,
  baseballBarAnchorRef,
  joiningLate = false,
}) {
  const videoRef = React.useRef(null);
  const presentationPreloadRef = React.useRef(null);
  const presentationPreloadRef2 = React.useRef(null);
  const presentationReadyKeyRef = React.useRef(null);
  const isSegmentSport = isBaseball(sportId) || isFootball(sportId);
  const presentationPreloadUrl =
    phase === 'presentation' && !joiningLate && isSegmentSport
      ? (Array.isArray(videoUrls) && videoUrls.length > 0 ? videoUrls[0] : videoUrl)
      : null;
  const presentationPreloadUrl2 =
    phase === 'presentation' && !joiningLate && isSegmentSport
      ? (Array.isArray(videoUrls) && videoUrls.length > 1 ? videoUrls[1] : null)
      : null;
  useHlsOrSrc(
    presentationPreloadRef,
    presentationPreloadUrl,
    avh,
    `presentation|${String(event?.event_id ?? eventResults?.event_id ?? '')}|${String(sportId ?? '')}|${String(presentationPreloadUrl ?? '')}|${String(avh ?? '')}`
  );
  useHlsOrSrc(
    presentationPreloadRef2,
    presentationPreloadUrl2,
    avh,
    `presentation2|${String(event?.event_id ?? eventResults?.event_id ?? '')}|${String(sportId ?? '')}|${String(presentationPreloadUrl2 ?? '')}|${String(avh ?? '')}`
  );

  /** CSS variables for mini-tv.css background-image URLs so they resolve when embedded (withBasePath). */
  const assetUrlStyle = {
    '--c7-mini-tv-url-baseball-bg-transition': `url(${withBasePath('/images/baseball/bg-transition.png')})`,
    '--c7-mini-tv-url-baseball-ball': `url(${withBasePath('/images/baseball/ball.png')})`,
  };

  React.useEffect(() => {
    return () => {
      [videoRef.current, presentationPreloadRef.current, presentationPreloadRef2.current].filter(Boolean).forEach((v) => {
        try {
          v.pause?.();
          v.removeAttribute?.('src');
          v.load?.();
        } catch {
          // ignore
        }
      });
    };
  }, []);

  React.useEffect(() => {
    presentationReadyKeyRef.current = null;
  }, [event?.event_id, eventResults?.event_id, phase]);

  React.useEffect(() => {
    if (phase !== 'presentation' || joiningLate || !presentationPreloadUrl || !presentationPreloadRef.current) return;
    const v1 = presentationPreloadRef.current;
    const v2 = presentationPreloadRef2.current;
    const needsBoth = presentationPreloadUrl2 != null && v2 != null;
    const readyKey = `${String(event?.event_id ?? eventResults?.event_id ?? '')}|${String(presentationPreloadUrl)}|${String(presentationPreloadUrl2 ?? '')}`;
    const readyFlags = { first: false, second: !needsBoth };
    const tryMarkReady = () => {
      if (!readyFlags.first || !readyFlags.second) return;
      if (presentationReadyKeyRef.current === readyKey) return;
      presentationReadyKeyRef.current = readyKey;
      onVideoReady?.();
    };
    const onFirstReady = () => { readyFlags.first = true; tryMarkReady(); };
    const onSecondReady = () => { readyFlags.second = true; tryMarkReady(); };
    v1.addEventListener('canplay', onFirstReady);
    if (v1.readyState >= 2) onFirstReady();
    if (needsBoth) {
      v2.addEventListener('canplay', onSecondReady);
      if (v2.readyState >= 2) onSecondReady();
    }
    return () => {
      v1.removeEventListener('canplay', onFirstReady);
      if (needsBoth && v2) v2.removeEventListener('canplay', onSecondReady);
    };
  }, [phase, joiningLate, presentationPreloadUrl, presentationPreloadUrl2, onVideoReady, event?.event_id, eventResults?.event_id]);

  React.useEffect(() => {
    if (phase !== 'execution' || !videoUrl || !videoRef.current) return;
    if (isRacing(sportId) && initialSeekTime != null && Number(initialSeekTime) > 0) return;
    const v = videoRef.current;
    const onCanPlay = () => {
      onVideoReady?.();
      v.play().catch((err) => console.warn('[MiniTv] play() rejected:', err?.message || err));
    };
    v.addEventListener('canplay', onCanPlay);
    v.play().catch((err) => console.warn('[MiniTv] play() rejected:', err?.message || err));
    return () => v.removeEventListener('canplay', onCanPlay);
  }, [phase, videoUrl, sportId, initialSeekTime, onVideoReady]);

  if (phase === 'presentation') {
    if (joiningLate) {
      return (
        <div className="c7-mini-tv-viewer c7-mini-tv-viewer--execution" style={assetUrlStyle}>
          <div className="c7-mini-tv-execution">
            <div className="c7-mini-tv-media-wrapper">
              <div className="c7-mini-tv-join-late-loader" aria-hidden>
                <div className="c7-mini-tv-join-late-loader__spinner" />
              </div>
              <PortaledEventBox eventId={event?.event_id ?? eventResults?.event_id} />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="c7-mini-tv-viewer c7-mini-tv-viewer--presentation" style={assetUrlStyle}>
        {presentationPreloadUrl && (
          <video
            ref={presentationPreloadRef}
            src={presentationPreloadUrl && !presentationPreloadUrl.includes('m3u8') ? presentationPreloadUrl : ''}
            playsInline
            muted
            preload="auto"
            aria-hidden
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
        )}
        {presentationPreloadUrl2 && (
          <video
            ref={presentationPreloadRef2}
            src={presentationPreloadUrl2 && !presentationPreloadUrl2.includes('m3u8') ? presentationPreloadUrl2 : ''}
            playsInline
            muted
            preload="auto"
            aria-hidden
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
        )}
        {isRacing(sportId) && <RacingPresentation sportId={sportId} />}
        {isBaseball(sportId) && <BaseballPresentation event={event} eventResults={eventResults} />}
        {isFootball(sportId) && <FootballPresentation event={event} eventResults={eventResults} />}
        {!isRacing(sportId) && !isBaseball(sportId) && !isFootball(sportId) && <RacingPresentation sportId={sportId} />}
      </div>
    );
  }

  if (phase === 'execution' && videoUrl) {
    const eventId = event?.event_id ?? eventResults?.event_id;
    return (
      <div className="c7-mini-tv-viewer c7-mini-tv-viewer--execution" style={assetUrlStyle}>
        {isRacing(sportId) && (
          <RacingExecution
            eventId={eventId}
            eventResults={eventResults}
            videoUrl={videoUrl}
            avh={avh}
            showevolution={showevolution}
            videoRef={videoRef}
            sportId={sportId}
            onExecutionEnded={onExecutionEnded}
            initialSeekTime={initialSeekTime}
            muted={muted}
          />
        )}
        {isBaseball(sportId) && (videoUrls?.length ? (
          <MiniTvBaseballExecution
            eventId={eventId}
            event={event}
            eventResults={eventResults}
            videoUrls={videoUrls}
            avh={avh}
            onVideoReady={onVideoReady}
            onExecutionEnded={onExecutionEnded}
            initialSegmentIndex={initialSegmentIndex}
            initialSegmentSeekSeconds={initialSegmentSeekSeconds}
            muted={muted}
            baseballBarAnchorRef={baseballBarAnchorRef}
          />
        ) : (
          <BaseballExecution eventId={eventId} videoUrl={videoUrl} avh={avh} videoRef={videoRef} onExecutionEnded={onExecutionEnded} muted={muted} />
        ))}
        {isFootball(sportId) && (videoUrls?.length ? (
          <MiniTvFootballExecution
            eventId={eventId}
            event={event}
            eventResults={eventResults}
            videoUrls={videoUrls}
            avh={avh}
            onVideoReady={onVideoReady}
            onExecutionEnded={onExecutionEnded}
            joinElapsedSeconds={joinElapsedSeconds}
            initialSegmentIndex={initialSegmentIndex}
            initialSegmentSeekSeconds={initialSegmentSeekSeconds}
            otherWeekEvents={otherWeekEvents}
            weekResults={weekResults}
            muted={muted}
          />
        ) : (
          <FootballExecution eventId={eventId} videoUrl={videoUrl} avh={avh} videoRef={videoRef} onExecutionEnded={onExecutionEnded} muted={muted} />
        ))}
        {!isRacing(sportId) && !isBaseball(sportId) && !isFootball(sportId) && (
          <MiniTvGenericVideo eventId={eventId} videoRef={videoRef} videoUrl={videoUrl} avh={avh} onExecutionEnded={onExecutionEnded} muted={muted} />
        )}
      </div>
    );
  }

  return null;
}
